// NautikaSeatOverlayAnchored.jsx
import React, { useMemo, useState } from "react";
import { LUXURY_X, LUXURY_Y, ROYAL_X, ROYAL_Y } from "./deckAnchors";

/* ------------------------------------------------------------
   Utility Helpers
------------------------------------------------------------ */

// Safe convert to object
const toObj = (v) => {
  if (!v) return {};
  if (typeof v === "object") return v;
  try {
    return JSON.parse(v);
  } catch {
    return {};
  }
};

// Normalize seat availability flags
const isTrue = (v) =>
  v === 1 || v === "1" || v === true || v === "true" || v === "Y";

// Assign seat status
const seatStatus = (s) => {
  const raw = String(s.status ?? s.seat_status ?? "").toLowerCase();
  if (isTrue(s.isBlocked) || raw.includes("block")) return "blocked";
  if (isTrue(s.isBooked) || raw.includes("book")) return "booked";
  if (isTrue(s.isAvailable) || raw.includes("avail")) return "available";
  return "available";
};

// Parse seat label -> { row, col }
const parseLabel = (label) => {
  if (!label) return null;
  const clean = label.toUpperCase().replace(/[\s_-]/g, "");
  const letter = clean.match(/[A-Z]+/);
  const number = clean.match(/[0-9]+/);
  if (!letter || !number) return null;
  return { col: letter[0].slice(-1), row: parseInt(number[0], 10) };
};

// Convert seatMap into seat objects array
const normalizeSeats = (mapLike) => {
  const map = toObj(mapLike);
  return Object.entries(map)
    .map(([key, raw]) => {
      const seat = raw || {};
      const label = String(seat.number ?? seat.label ?? key);
      const parsed = parseLabel(label);
      return parsed
        ? { label, row: parsed.row, col: parsed.col, status: seatStatus(seat) }
        : null;
    })
    .filter(Boolean);
};

// Build dynamic fallback coordinates
const dynamicAxis = (vals, start = 20, end = 80) => {
  const uniq = Array.from(new Set(vals));
  if (!uniq.length) return {};
  uniq.sort();
  const step = uniq.length === 1 ? 0 : (end - start) / (uniq.length - 1);
  return uniq.reduce((acc, val, idx) => {
    acc[val] = start + idx * step;
    return acc;
  }, {});
};

// Always return valid coordinate
const coord = (anchor, fallback, fake = 50) =>
  anchor ?? fallback ?? fake;

// Compute SVG viewBox
const buildViewBox = (seats, AX, AY, dx, dy) => {
  let xMin = 100,
    yMin = 100,
    xMax = 0,
    yMax = 0;

  seats.forEach((s) => {
    const x = coord(AX[s.col], dx[s.col]);
    const y = coord(AY[s.row], dy[s.row]);
    xMin = Math.min(xMin, x);
    yMin = Math.min(yMin, y);
    xMax = Math.max(xMax, x);
    yMax = Math.max(yMax, y);
  });

  return `${xMin - 10} ${yMin - 10} ${xMax - xMin + 20} ${yMax - yMin + 20}`;
};

/* ------------------------------------------------------------
   MAIN COMPONENT (Rewritten clean version)
------------------------------------------------------------ */

export default function NautikaSeatOverlayAnchored({
  seatMap,
  classCode,          // "B" Royal, "P" Luxury
  passengers = 1,
  bgUrl,
  onChange,
  seatScale = 1.0,
  mobileHeight = 860,
}) {
  /* ------------------------------------------------------------
     Normalized seats & axis
  ------------------------------------------------------------ */
  const seats = useMemo(() => normalizeSeats(seatMap), [seatMap]);

  const AX = classCode === "P" ? LUXURY_X : ROYAL_X;
  const AY = classCode === "P" ? LUXURY_Y : ROYAL_Y;

  const dynX = useMemo(() => dynamicAxis(seats.map((s) => s.col)), [seats]);
  const dynY = useMemo(() => dynamicAxis(seats.map((s) => s.row)), [seats]);

  const [selected, setSelected] = useState([]);

  /* ------------------------------------------------------------
     Seat drawing order
  ------------------------------------------------------------ */
  const sortedSeats = useMemo(() => {
    if (classCode !== "P") return seats;
    return [...seats].sort((a, b) => a.row - b.row || a.col.localeCompare(b.col));
  }, [seats, classCode]);

  /* ------------------------------------------------------------
     Seat selection logic
  ------------------------------------------------------------ */
  const handleToggle = (seat) => {
    if (seat.status !== "available") return;

    if (selected.includes(seat.label)) {
      const updated = selected.filter((s) => s !== seat.label);
      setSelected(updated);
      onChange?.(updated);
      return;
    }

    if (selected.length >= passengers) {
      alert(`You may select only ${passengers} seat(s).`);
      return;
    }

    const updated = [...selected, seat.label];
    setSelected(updated);
    onChange?.(updated);
  };

  /* ------------------------------------------------------------
     SVG seat size
  ------------------------------------------------------------ */
  const BASE = 5 * seatScale;
  const HALF = BASE / 2;
  const RADIUS = BASE * 0.2;
  const FONT = BASE * 0.35;

  const viewBox = useMemo(
    () => buildViewBox(seats, AX, AY, dynX, dynY),
    [seats, AX, AY, dynX, dynY]
  );

  /* ------------------------------------------------------------
     RENDER
  ------------------------------------------------------------ */

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div
        className="deck-stage"
        style={{
          position: "relative",
          width: "90%",
          maxWidth: 560,
          margin: "0 auto",
          minHeight: mobileHeight,
        }}
      >
        {/* Background Deck Image */}
        <img
          src={bgUrl}
          alt="Deck Layout"
          style={{
            width: "100%",
            height: "100%",
            minHeight: 800,
            objectFit: "cover",
            display: "block",
          }}
        />

        {/* Seat Overlay */}
        <svg
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid meet"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {sortedSeats.map((s) => {
            const x = coord(AX[s.col], dynX[s.col]);
            const y = coord(AY[s.row], dynY[s.row]);

            const isSelected = selected.includes(s.label);
            const fill =
              isSelected
                ? "#2563eb"
                : s.status === "booked"
                ? "#d1d5db"
                : s.status === "blocked"
                ? "#ffcccc"
                : "#ffffff";

            return (
              <g
                key={s.label}
                onClick={() => handleToggle(s)}
                style={{ cursor: "pointer", pointerEvents: "auto" }}
              >
                <rect
                  x={x - HALF}
                  y={y - HALF}
                  width={BASE}
                  height={BASE}
                  fill={fill}
                  stroke="#000"
                  strokeWidth="0.4"
                  rx={RADIUS}
                />
                <text
                  x={x}
                  y={y + BASE * 0.15}
                  fontSize={FONT}
                  textAnchor="middle"
                  fill={isSelected ? "#fff" : "#000"}
                >
                  {s.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
